import { FocusEvent, useEffect, useRef } from "react";

// When the focused button is removed from the page, focus would fall back to
// the top of the page: move it to the element holding the returned ref
// (focusable with tabIndex={-1}) instead (RGAA 12.8).
// handleFocus and handleBlur are bound to the container of the button and of
// the target, so that the focus is only moved for the user who was on the
// button, never taken from a user reading elsewhere.
export function useFocusOnButtonRemoval<T extends HTMLElement>(
    isButtonDisplayed: boolean,
) {
    const targetRef = useRef<T>(null);
    const wasButtonFocusedRef = useRef(false);

    useEffect(() => {
        if (isButtonDisplayed || !wasButtonFocusedRef.current) {
            return;
        }
        wasButtonFocusedRef.current = false;
        // The focus already moved elsewhere: leave it there
        if (
            document.activeElement &&
            document.activeElement !== document.body
        ) {
            return;
        }
        targetRef.current?.focus();
    }, [isButtonDisplayed]);

    function handleFocus(event: FocusEvent<HTMLElement>) {
        wasButtonFocusedRef.current = event.target !== targetRef.current;
    }

    function handleBlur(event: FocusEvent<HTMLElement>) {
        // Without a related target, the focus may have been lost because the
        // button was removed: keep the flag for the effect
        if (null !== event.relatedTarget) {
            wasButtonFocusedRef.current = false;
        }
    }

    return { handleBlur, handleFocus, targetRef };
}
